import React from 'react';
import { 
  Users, 
  Target, 
  CheckCircle,
  Building2,
  GraduationCap
} from 'lucide-react';
import Button from '../../components/Button/Button';
import Card from '../../components/Card/Card';
import './Services.css';

const Services: React.FC = () => {

  const services = [
    {
      id: 'memberships',
      icon: <GraduationCap size={40} />,
      title: 'Memberships',
      subtitle: 'Continuous networking and educational opportunities',
      description: 'Join our membership community to access continuous networking and educational opportunities. Members benefit from monthly presentations from industry leaders, exclusive networking events, job opportunities, and discounted access to our programs. We offer memberships for students, professionals, and organizations.',
      targetAudience: 'Students, professionals, and organizations in racquet sports',
      features: [
        'Monthly presentations from industry leaders',
        'Exclusive networking events',
        'Access to job opportunities',
        'Educational resources and webinars',
        'Discounts on BLXM course and retreats',
        'Mentorship opportunities',
      ],
    },
    {
      id: 'blxm',
      icon: <Users size={40} />,
      title: 'Baseline Executive Mastermind Course (BLXM)',
      subtitle: 'A leadership course designed to elevate careers',
      description: 'The course is an immersive, twelve-week professional development program created by the Baseline team. Participants engage in dynamic live sessions, personalized coaching, real-world case studies, and collaborative discussions with a network of driven, like-minded racquets professionals.',
      targetAudience: 'Racquet sports enthusiasts worldwide',
      features: [
        '10-module online course',
        'Keynote speakers',
        'Performance tracking and metrics',
        'Access to exclusive training content',
        'Professional network and mentorship',
      ],
    },
    {
      id: 'club-consulting',
      icon: <Target size={40} />,
      title: 'Private Club Consulting',
      subtitle: 'Transform underperforming clubs into thriving centers',
      description: 'We specialize in diagnosing and solving the challenges for racquets programs at various clubs. Our comprehensive consulting services address everything from implementing proven systems to increasing revenues and profitability.',
      targetAudience: 'Owners and club managers',
      features: [
        'Comprehensive club assessment and diagnosis',
        'Strategic planning and implementation',
        'Revenue optimization strategies',
        'Member retention and acquisition programs',
        'Staff training and development',
        'Operational efficiency improvements',
      ],
    },
    {
      id: 'retreats',
      icon: <Building2 size={40} />,
      title: 'Retreats',
      subtitle: 'Exclusive annual retreats hosted by Baseline',
      description: 'Retreats hosted at premier destinations, crafted to connect current and former members with top industry leaders and prospective employers in the racquet sports sector. These immersive weekends feature engaging networking roundtables, elegant dinners, refreshing drinks, and exciting activities, fostering meaningful connections and professional growth in a dynamic, relaxed setting.',
      targetAudience: 'Current and former members, industry leaders, and prospective employers',
      features: [
        'Access to industry leaders and potential employers',
        'Networking roundtables',
        'Exclusive dining experiences',
        'Career opportunities',
        'Social activities',
        'Premier destinations',
      ],
    },
  ];

  return (
    <div className="services">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <div className="services-hero-content">
            <h1 className="services-hero-title">Our Services</h1>
            <p className="services-hero-subtitle">
              At Baseline, we offer a comprehensive suite of services designed to address every challenge and opportunity in the racquet sports industry. From professional development to large-scale club projects, our expertise covers the full spectrum of needs in this dynamic market.
            </p>
          </div>
        </div>
      </section>


      {/* Individual Service Sections */}
      {services.map((service, index) => (
        <section
          key={service.id}
          id={service.id}
          className={`section service-detail ${index % 2 === 1 ? 'service-detail-alt' : ''}`}
        >
          <div className="container">
            <div className="service-detail-grid">
              <div className="service-detail-content">
                <div className="service-detail-header">
                  <div className="service-detail-icon">{service.icon}</div>
                  <div>
                    <h2 className="service-detail-title">{service.title}</h2>
                    <p className="service-detail-subtitle">{service.subtitle}</p>
                  </div>
                </div>

                <p className="service-detail-description">{service.description}</p>

                <div className="service-detail-audience">
                  <strong>Target Audience:</strong> {service.targetAudience}
                </div>
              </div>

              <div className="service-detail-benefits">
                <Card variant="elevated">
                  <div className="benefits-card">
                    <h3 className="benefits-title">Key Features</h3>
                    <ul className="benefits-list">
                      {service.features.map((feature) => (
                        <li key={feature}>
                          <CheckCircle size={20} className="benefit-icon" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="benefits-cta">
                      {service.id === 'memberships' ? (
                        <Button variant="primary" size="medium" href="/memberships" fullWidth>
                          View Memberships
                        </Button>
                      ) : service.id === 'blxm' ? (
                        <Button variant="primary" size="medium" href="/blxm-course" fullWidth>
                          View Course
                        </Button>
                      ) : (
                        <Button variant="primary" size="medium" href="https://calendar.app.google/zeaz1ocNpttMocbv7" fullWidth>
                          Schedule a Consultation
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Process Section */}
      <section className="section process-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title text-center">Our Process</h2>
            <p className="section-subtitle text-center">
              A proven methodology that delivers results
            </p>
          </div>

          <div className="process-grid">
            <div className="process-step">
              <div className="process-number">1</div>
              <h3 className="process-title">Discovery</h3>
              <p className="process-description">
                Book a call with the Baseline team for a comprehensive assessment to understand your unique challenges and goals.
              </p>
            </div>

            <div className="process-step">
              <div className="process-number">2</div>
              <h3 className="process-title">Strategy</h3>
              <p className="process-description">
                Our experts design tailored strategies that drive sustainable growth in your specific market.
              </p>
            </div>

            <div className="process-step">
              <div className="process-number">3</div>
              <h3 className="process-title">Growth</h3>
              <p className="process-description">
                We work alongside you to execute the strategy, providing hands-on support every step of the way.
              </p>
            </div>

            <div className="process-step">
              <div className="process-number">4</div>
              <h3 className="process-title">Results</h3>
              <p className="process-description">
                We measure success through tangible outcomes and continue to optimize for long-term success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section services-cta-section">
        <div className="container">
          <div className="services-cta-content">
            <h2 className="cta-title">Ready to Get Started?</h2>
            <p className="cta-subtitle">
              Let's discuss how our services can help you achieve your goals
            </p>
            <p className="cta-note">
              All services are customized to your specific needs. Contact us for a personalized consultation and pricing.
            </p>
            <Button variant="primary" size="large" href="https://calendar.app.google/zeaz1ocNpttMocbv7">
              Schedule Your Free Consultation
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
